import { ChatbotUiState } from "@/components/enums/UiState";
import ButtonChat from "@/components/shared/futureChat/buttons/ButtonChat";
import Card from "@/components/shared/futureChat/cards/Card";
import { TrackingContext } from "@/components/shared/tracking/Tracking";
import { cn } from "@/lib/utils";
import ExportedImage from "next-image-export-optimizer";
import { useContext, useEffect, useState } from "react";
import store from "stores/store";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";

type LandingProps = {
    onLetsGo: () => void;
    onHowItWorks: () => void;
    onTakeSelfie: () => void;
    setSelfieOrCall: React.Dispatch<React.SetStateAction<"selfie" | "call">>;
};

const Landing: React.FC<LandingProps> = ({ onLetsGo, onHowItWorks, onTakeSelfie, setSelfieOrCall }) => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const { chatbotLocalized, globalLocalized, landingLocalized, updateRenderChatbotContent, globalEventLocalized } = store;

    const [windowWidth, setWindowWidth] = useState(0);

    const { screenView } = useContext(TrackingContext);

    useEffect(() => {
        setWindowWidth(window.innerWidth);

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);

        screenView('1.1 - Start')

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const getImageSize = () => {
        if (windowWidth < 480) {
            return chatbotLocalized?.landingBg320px;
        } else if (windowWidth < 768) {
            return chatbotLocalized?.landingBg480px;
        } else if (windowWidth < 1024) {
            return chatbotLocalized?.landingBg768px;
        } else if (windowWidth < 1280) {
            return chatbotLocalized?.landingBg1024px;
        } else if (windowWidth < 1920) {
            return chatbotLocalized?.landingBg1280px;
        } else if (windowWidth >= 1920) {
            return chatbotLocalized?.landingBg1920px;
        }
    };

    const background = getImageSize();
    const landingButtonOneIcon = globalEventLocalized?.landingButton1.icon;
    const landingButtonTwoIcon = globalEventLocalized?.landingButton2.icon;
    const howItWorksIcon = landingLocalized?.buttonTwo.icon;

    return (
        <div className="flex h-full w-full flex-col items-center">
            {background !== undefined && background !== null && (
                <div className={cn("absolute h-full w-full")}>
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(background)}
                        alt={getAltTextFromGeneralImage(background)}
                        layout="fill"
                        objectFit="cover"
                    />
                </div>
            )}
            {globalLocalized?.logo !== undefined && globalLocalized?.logo !== null && (
                <div
                    className={cn(
                        "relative h-[158px] w-[247px]",
                        "2xs:h-[237px] 2xs:w-[371px]",
                        // "4k:h-[1068px] 4k:w-[1668px]",
                    )}
                >
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(globalLocalized.logo)}
                        alt={getAltTextFromGeneralImage(globalLocalized.logo)}
                        layout="fill"
                        objectFit="contain"
                    />
                </div>
            )}
            <div
                className={cn(
                    "absolute bottom-[46px] flex w-full flex-col gap-[12px] px-[16px]",
                    "2xs:bottom-[68px] 2xs:w-[364px] 2xs:px-0"
                )}
            >
                <ButtonChat
                    onClick={() => {
                        onTakeSelfie();
                        setSelfieOrCall("selfie");
                    }}
                    iconRight={
                        landingButtonOneIcon ? (
                            <ExportedImage
                                src={getImageUrlFromGeneralImage(landingButtonOneIcon)}
                                alt={getAltTextFromGeneralImage(landingButtonOneIcon)}
                                layout="fill"
                            />
                        ) : undefined
                    }
                    variant="primary"
                >
                    {globalEventLocalized?.landingButton1.text}
                </ButtonChat>
                <ButtonChat
                    onClick={() => {
                        onLetsGo();
                        setSelfieOrCall("call");
                    }}
                    iconRight={
                        landingButtonTwoIcon ? (
                            <ExportedImage
                                src={getImageUrlFromGeneralImage(landingButtonTwoIcon)}
                                alt={getAltTextFromGeneralImage(landingButtonTwoIcon)}
                                layout="fill"
                            />
                        ) : undefined
                    }
                    variant="primary"
                >
                    {globalEventLocalized?.landingButton2.text}
                </ButtonChat>
                <ButtonChat
                    onClick={onHowItWorks}
                    iconRight={
                        howItWorksIcon ? (
                            <ExportedImage
                                src={getImageUrlFromGeneralImage(howItWorksIcon)}
                                alt={getAltTextFromGeneralImage(howItWorksIcon)}
                                layout="fill"
                            />
                        ) : undefined
                    }
                    variant="secondary"
                >
                    {landingLocalized?.buttonTwo.text}
                </ButtonChat>
            </div>
            <div className="absolute bottom-[20px] text-[12px] md:bottom-[22.67px] flex w-full justify-center gap-[24px] px-[430px] font-frutigerBold font-bold leading-[100%] text-dark-500 underline">
                <div onClick={() => updateRenderChatbotContent(ChatbotUiState.POLICY)} className="hover:cursor-pointer">
                    {globalLocalized?.dataProtectionInformation}
                </div>
                <div onClick={() => updateRenderChatbotContent(ChatbotUiState.IMPRINT)} className="hover:cursor-pointer">{globalLocalized?.imprint}</div>
            </div>
        </div>
    );
};

export default Landing;
